"use client";

import { Download } from "lucide-react";
import { useState } from "react";
import { cn } from "@/lib/format";

/**
 * Triggers a brochure PDF download for the given package slug. The PDF is
 * generated server-side at /api/brochure/:slug (brand-agnostic — brand pulled
 * from tenant Settings there), so this just fetches the stream and saves it.
 */
export function DownloadBrochureButton({
  slug,
  className,
}: {
  slug: string;
  className?: string;
}) {
  const [isLoading, setIsLoading] = useState(false);

  async function handleDownload() {
    setIsLoading(true);
    try {
      const res = await fetch(`/api/brochure/${encodeURIComponent(slug)}`);
      if (!res.ok) {
        console.error("Brochure generation failed", res.status);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug}-brochure.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading brochure:", err);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <button
      type="button"
      onClick={handleDownload}
      disabled={isLoading}
      className={cn(
        "inline-flex whitespace-nowrap items-center gap-2 rounded-full border border-headings/25 bg-white px-3.5 py-2 text-xs font-bold text-headings transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
    >
      {isLoading ? (
        <svg
          className="h-3.5 w-3.5 animate-spin text-primary"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      ) : (
        <Download size={14} className="text-primary" />
      )}
      <span className="hidden sm:inline">
        {isLoading ? "Generating…" : "Download PDF"}
      </span>
      <span className="sm:hidden">PDF</span>
    </button>
  );
}

export default DownloadBrochureButton;
